/**
 * services/settlement/settlement.service.ts
 * Leaving custodians who still hold company money. Gated on the offboarding
 * feed (412 + `offboard_feed`).
 */
import axiosInstance from '../../api/axiosInstance';
import { Cash_Settlement_Resolve_Url, Cash_Settlements_Url } from '../../api/endpoints';
import { parseResponse } from '../../schemas/common';
import {
  separationsResponseSchema,
  type Separation,
  type SettlementResponse,
} from '../../schemas/settlement';

/**
 * Leavers still holding cash, the per-currency exposure, and the gate map.
 * Returned whole for the same reason as the sync log — the gate rides in the
 * body with a 200, and `exposure` is a figure the screen shows.
 */
export async function fetchSettlements(state?: string): Promise<SettlementResponse> {
  const { data } = await axiosInstance.get(Cash_Settlements_Url, { params: { state } });
  return parseResponse(separationsResponseSchema, data, 'GET /v1/settlement');
}

/**
 * Settle a leaver's open balance, or waive it with a reason. One alert per open
 * assignment, so settling one does not clear the others a person holds.
 */
export async function resolveSettlement(
  id: string,
  action: 'settle' | 'waive',
  reason?: string,
): Promise<unknown> {
  if (action === 'waive' && !reason?.trim()) {
    throw new Error('Waiving a settlement requires a reason.');
  }
  const { data } = await axiosInstance.post(Cash_Settlement_Resolve_Url(id), {
    action,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  });
  return data;
}

export type { Separation };
